import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal, Field, Select } from "../../components/ui-modal";
import { ProjectCard } from "../../components/ProjectCard";
import InstallmentPlanBuilder from "../../components/InstallmentPlanBuilder";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

const MONTHS = [
  { v: 1, l: "Janvier" }, { v: 2, l: "Février" }, { v: 3, l: "Mars" }, { v: 4, l: "Avril" },
  { v: 5, l: "Mai" }, { v: 6, l: "Juin" }, { v: 7, l: "Juillet" }, { v: 8, l: "Août" },
  { v: 9, l: "Septembre" }, { v: 10, l: "Octobre" }, { v: 11, l: "Novembre" }, { v: 12, l: "Décembre" },
];
const YEARS = Array.from({ length: 6 }, (_, i) => 2024 + i);
const CURRENCIES = [{ v: "EUR", l: "€ EUR" }, { v: "USD", l: "$ USD" }, { v: "AED", l: "AED" }];
const NOW = new Date();

const ModuleDescription = ({ module, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  useEffect(() => { setVal(module?.description || ""); }, [module?.description]);
  if (!module) return null;
  const save = async () => {
    try {
      await api.put(`/admin/modules/${module.id}`, { description: val });
      toast.success("Description mise à jour");
      setEditing(false);
      onSaved && onSaved();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };
  return (
    <div className="-mt-3 mb-6 max-w-3xl" data-testid="module-description">
      {editing ? (
        <div className="bg-sable border border-marine/15 rounded-sm p-3">
          <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} data-testid="module-description-input"
            className="inp w-full resize-y" placeholder="Décrivez ce module pour vos clients (visible sur leur portail)…" />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={save} data-testid="module-description-save" className="bg-marine text-white rounded-sm px-4 py-1.5 text-sm hover:bg-marine-hover transition-colors">Enregistrer</button>
            <button onClick={() => { setEditing(false); setVal(module.description || ""); }} className="text-sm text-marine/80 hover:opacity-70 px-2">Annuler</button>
            <span className="ml-auto text-[11px] text-marine/80 inline-flex items-center gap-1"><Icons.Eye size={16} className="text-marine" /> Visible sur le portail client</span>
          </div>
        </div>
      ) : (
        <button onClick={() => setEditing(true)} data-testid="module-description-edit"
          className="group text-left w-full flex items-start gap-2 text-sm text-marine/80 hover:text-marine transition-colors">
          <Icons.Pencil size={16} className="mt-0.5 opacity-0 group-hover:opacity-100 shrink-0 text-bordeaux" />
          <span>{module.description ? module.description : <span className="italic text-gris-texte">Ajouter une description (visible sur le portail client)…</span>}</span>
        </button>
      )}
    </div>
  );
};

export default function AdminModuleDetail() {
  const { confirm } = useConfirm();
  const { key } = useParams();
  const nav = useNavigate();
  const [module, setModule] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [pwfs, setPwfs] = useState([]);
  const [types, setTypes] = useState({});
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [fClient, setFClient] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [fYear, setFYear] = useState("");
  const [form, setForm] = useState({ name: "", amount: "", workflow_id: "", period_month: "", period_year: "" });

  const load = useCallback(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data.filter((p) => p.type === key)));
    api.get("/admin/modules").then((r) => setModule(r.data.find((m) => m.key === key) || null));
  }, [key]);

  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/project-workflows").then((r) => setPwfs(r.data));
    api.get("/project-types").then((r) => setTypes(r.data));
  }, [load]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const typeWorkflows = pwfs.filter((w) => w.type === key);

  const openCreate = () => {
    const def = typeWorkflows.find((w) => w.is_default) || typeWorkflows[0];
    setForm({ name: module?.label || "", amount: "", currency: "EUR", workflow_id: def ? def.id : "",
      period_month: NOW.getMonth() + 1, period_year: NOW.getFullYear(), recurring: false, installments: [] });
    setCreating(true);
  };
  const create = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Sélectionnez une entreprise");
    await api.post("/admin/projects", {
      client_id: form.client_id, type: key, name: form.name, amount: parseFloat(form.amount) || 0,
      currency: form.currency || "EUR",
      workflow_id: form.workflow_id || null, recurring: !!form.recurring,
      period_month: form.period_month ? Number(form.period_month) : null,
      period_year: form.period_year ? Number(form.period_year) : null,
      installments: (form.installments || []).filter((r) => r.label || r.amount).map((r) => ({ label: r.label, amount: parseFloat(r.amount) || 0, due_date: r.due_date || null })),
    });
    toast.success("Projet créé"); setCreating(false); load();
  };

  const duplicate = async (p) => {
    try { await api.post(`/admin/projects/${p.id}/duplicate`); toast.success("Projet dupliqué"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la duplication"); }
  };
  const duplicateNextMonth = async (p) => {
    try { await api.post(`/admin/projects/${p.id}/duplicate?advance_period=true`); toast.success("Reconduit pour le mois suivant"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de la reconduction"); }
  };
  const deleteProject = async (p) => {
    if (!(await confirm({ title: `Supprimer définitivement le projet « ${p.name} » ?`, message: "Cette action est irréversible.", confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/projects/${p.id}`); toast.success("Projet supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const openEdit = (p) => {
    setEditForm({ name: p.name || "", amount: p.amount || "", currency: p.currency || "EUR", period_month: p.period_month || "", period_year: p.period_year || "", recurring: !!p.recurring });
    setEditRow(p);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/projects/${editRow.id}`, {
        name: editForm.name, amount: parseFloat(editForm.amount) || 0, currency: editForm.currency || "EUR", recurring: !!editForm.recurring,
        period_month: editForm.period_month ? Number(editForm.period_month) : null,
        period_year: editForm.period_year ? Number(editForm.period_year) : null,
      });
      toast.success("Projet mis à jour"); setEditRow(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Modification impossible"); }
  };

  const filtered = projects.filter((p) => {
    if (fClient && p.client_id !== fClient) return false;
    if (fMonth && String(p.period_month) !== fMonth) return false;
    if (fYear && String(p.period_year) !== fYear) return false;
    return true;
  });
  const hasFilters = fClient || fMonth || fYear;

  return (
    <div>
      <button onClick={() => nav("/admin/modules")} data-testid="back-to-modules" className="text-sm text-marine/80 hover:text-marine inline-flex items-center gap-1 mb-3"><Icons.ChevronLeft size={16} className="text-marine" /> Tous les modules</button>
      <PageTitle overline="Module" title={module?.label || key}
        action={<button onClick={openCreate} data-testid="new-project-btn" className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover flex items-center gap-2"><Icons.Plus size={16} /> Nouveau projet</button>} />

      <ModuleDescription module={module} onSaved={load} />

      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-6" data-testid="module-filters">
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Filtrer par entreprise" value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="filter-client" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Toutes les entreprises</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select aria-label="Filtrer par mois" value={fMonth} onChange={(e) => setFMonth(e.target.value)} data-testid="filter-month" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Tous les mois</option>
            {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select aria-label="Filtrer par année" value={fYear} onChange={(e) => setFYear(e.target.value)} data-testid="filter-year" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Toutes les années</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {hasFilters ? <button onClick={() => { setFClient(""); setFMonth(""); setFYear(""); }} data-testid="filter-reset" className="text-xs px-3 py-2 text-rouge hover:underline inline-flex items-center gap-1"><Icons.X size={16} /> Réinitialiser</button> : null}
          <span className="ml-auto text-xs text-marine/80" data-testid="module-project-count">{filtered.length} / {projects.length} projet(s)</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <ProjectCard key={p.id} project={p} clientName={clientName} onOpen={(x) => nav(`/admin/projets/${x.id}`)} onDuplicate={duplicate} onDuplicateNextMonth={duplicateNextMonth} onEdit={openEdit} onDelete={deleteProject} />
        ))}
        {projects.length === 0 && <p className="text-sm text-marine/80">Aucun projet dans ce module. Créez-en un pour démarrer.</p>}
        {projects.length > 0 && filtered.length === 0 && <p className="text-sm text-marine/80">Aucun projet ne correspond aux filtres.</p>}
      </div>

      {creating && (
        <Modal onClose={() => setCreating(false)} title={`Nouveau projet — ${module?.label || ""}`}>
          <form onSubmit={create} className="space-y-3">
            <Select label="Entreprise" value={form.client_id || ""} onChange={(v) => setForm({ ...form, client_id: v })} options={[{ v: "", l: "— Sélectionner —" }, ...clients.map((c) => ({ v: c.id, l: c.name }))]} testid="project-client" />
            {typeWorkflows.length > 0 && (
              <Select label="Workflow appliqué" value={form.workflow_id} onChange={(v) => setForm({ ...form, workflow_id: v })} options={typeWorkflows.map((w) => ({ v: w.id, l: `${w.name}${w.is_default ? " (défaut)" : ""}` }))} testid="project-workflow" />
            )}
            <Field label="Nom du projet"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" className="inp" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Field label="Montant facturé"><input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="project-amount" className="inp" placeholder="Ex : 4500" /></Field></div>
              <Select label="Devise" value={form.currency || "EUR"} onChange={(v) => setForm({ ...form, currency: v })} options={CURRENCIES} testid="project-currency" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Mois (période)" value={String(form.period_month)} onChange={(v) => setForm({ ...form, period_month: v })} options={[{ v: "", l: "—" }, ...MONTHS.map((m) => ({ v: String(m.v), l: m.l }))]} testid="project-period-month" />
              <Select label="Année (période)" value={String(form.period_year)} onChange={(v) => setForm({ ...form, period_year: v })} options={[{ v: "", l: "—" }, ...YEARS.map((y) => ({ v: String(y), l: String(y) }))]} testid="project-period-year" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer bg-bordeaux-50 border border-bordeaux/20 rounded-sm p-3">
              <input type="checkbox" checked={!!form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} data-testid="project-recurring" className="mt-0.5 accent-bordeaux" />
              <span className="text-sm text-marine"><span className="font-medium">Abonnement mensuel</span> — reconduction automatique le mois suivant. Une notification (tâche) est créée à chaque reconduction.</span>
            </label>
            <InstallmentPlanBuilder total={form.amount} value={form.installments} onChange={(v) => setForm({ ...form, installments: v })} />
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="project-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Créer</button></div>
          </form>
        </Modal>
      )}
      {editRow && (
        <Modal onClose={() => setEditRow(null)} title="Modifier le projet">
          <form onSubmit={saveEdit} className="space-y-3">
            <Field label="Nom du projet"><input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-project-name" className="inp" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Field label="Montant facturé"><input type="number" min="0" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="edit-project-amount" className="inp" /></Field></div>
              <Select label="Devise" value={editForm.currency || "EUR"} onChange={(v) => setEditForm({ ...editForm, currency: v })} options={CURRENCIES} testid="edit-project-currency" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Mois (période)" value={String(editForm.period_month)} onChange={(v) => setEditForm({ ...editForm, period_month: v })} options={[{ v: "", l: "—" }, ...MONTHS.map((m) => ({ v: String(m.v), l: m.l }))]} testid="edit-project-period-month" />
              <Select label="Année (période)" value={String(editForm.period_year)} onChange={(v) => setEditForm({ ...editForm, period_year: v })} options={[{ v: "", l: "—" }, ...YEARS.map((y) => ({ v: String(y), l: String(y) }))]} testid="edit-project-period-year" />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer bg-bordeaux-50 border border-bordeaux/20 rounded-sm p-3">
              <input type="checkbox" checked={!!editForm.recurring} onChange={(e) => setEditForm({ ...editForm, recurring: e.target.checked })} data-testid="edit-project-recurring" className="mt-0.5 accent-bordeaux" />
              <span className="text-sm text-marine"><span className="font-medium">Abonnement mensuel</span> — reconduction automatique le mois suivant. Décochez pour arrêter l'abonnement (ex : fin d'accord client).</span>
            </label>
            <button type="button" onClick={() => nav(`/admin/projets/${editRow.id}?vue=facturation`)} data-testid="edit-goto-billing" className="w-full flex items-center justify-between gap-2 border border-bordeaux/30 text-bordeaux rounded-sm px-3 py-2.5 text-sm hover:bg-bordeaux hover:text-white transition-colors">
              <span className="inline-flex items-center gap-2"><Icons.Wallet size={16} /> Gérer l'échéancier de paiement (montants, dates, échéances)</span>
              <Icons.ArrowRight size={16} />
            </button>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setEditRow(null)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="edit-project-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Enregistrer</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
